import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  Send, 
  Bot, 
  User, 
  Loader2, 
  Crown,
  AlertTriangle,
  RefreshCw,
  Star,
  Trophy,
  Target,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Zap,
  History,
  BarChart3
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import DashboardLayout from "../../components/DashboardLayout";
import TrialBlocker from "../../components/TrialBlocker";
import { TravelPromoBanner } from "../../components/Client/PlanSection";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterviewSimulator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [scores, setScores] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [approvalPercentage, setApprovalPercentage] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const scrollRef = useRef(null);

  const isPremiumOrVip = user?.plan_type === "premium" || user?.plan_type === "vip" || user?.plan_type === "advanced";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isPremiumOrVip) {
      fetchSessions();
    }
  }, []);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/interview-simulator/sessions`);
      setSessions(res.data.sessions || []);
      setRemainingAttempts(res.data.remaining_attempts);
      setIsUnlimited(res.data.is_unlimited);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const startNewSimulation = () => {
    if (!isUnlimited && remainingAttempts !== null && remainingAttempts <= 0) {
      toast.error("Has alcanzado el límite de simulaciones este mes. Actualiza a VIP para ilimitadas.");
      return;
    }
    
    setSessionId(null);
    setMessages([{
      role: "assistant",
      content: "Buenos días. Soy el oficial consular. Por favor, tome asiento. Voy a hacerle algunas preguntas sobre su solicitud de visa B1/B2.\n\n¿Cuál es el propósito principal de su viaje a Estados Unidos?"
    }]);
    setQuestionNumber(0);
    setScores([]);
    setIsFinished(false);
    setApprovalPercentage(null);
    setShowHistory(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/interview-simulator`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(res.data.session_id);
      setQuestionNumber(res.data.question_number);
      
      if (res.data.current_score) {
        setScores(prev => [...prev, res.data.current_score]);
      }
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: res.data.response,
        score: res.data.current_score
      }]);
      
      if (res.data.is_final) {
        setIsFinished(true);
        setApprovalPercentage(res.data.approval_percentage);
        fetchSessions(); // Refresh attempts count
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "No tienes acceso a esta función");
      } else {
        toast.error("Error al enviar mensaje. Intenta de nuevo.");
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return "text-emerald-500";
    if (score >= 4) return "text-amber-500";
    return "text-red-500";
  };

  const getScoreStars = (score) => {
    if (score >= 7) return "⭐⭐⭐";
    if (score >= 4) return "⭐⭐";
    return "⭐";
  };

  const averageScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;

  // Not premium or VIP
  if (!isPremiumOrVip) {
    return (
      <TrialBlocker feature="Simulador de Entrevista">
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-navy-900 mb-4">
            Simulador de Entrevista Consular
          </h1>
          <p className="text-navy-600 text-lg mb-8">
            Practica tu entrevista con nuestra IA avanzada que actúa como un oficial real y recibe una evaluación con porcentaje de aprobación.
          </p>
          <div className="bg-white rounded-2xl border border-navy-100 p-6 mb-8">
            <h3 className="font-semibold text-navy-900 mb-4">Incluye:</h3>
            <ul className="text-left space-y-3 text-navy-600">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Entrevista realista con IA (8 preguntas)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Feedback en tiempo real por cada respuesta
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Porcentaje de aprobación estimado
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Historial de mejora y progreso
              </li>
            </ul>
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <Zap className="w-6 h-6 text-blue-600 mb-2" />
                <p className="font-semibold text-blue-900">Premium</p>
                <p className="text-sm text-blue-700">3 simulaciones/mes</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <Crown className="w-6 h-6 text-amber-600 mb-2" />
                <p className="font-semibold text-amber-900">VIP</p>
                <p className="text-sm text-amber-700">Simulaciones ilimitadas</p>
              </div>
            </div>
          </div>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        </div>
      </DashboardLayout>
      </TrialBlocker>
    );
  }

  // History view
  if (showHistory) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setShowHistory(false)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <h1 className="text-2xl font-bold text-navy-900">Historial de Simulaciones</h1>
            </div>
          </div>
          
          {loadingSessions ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-navy-100">
              <History className="w-12 h-12 text-navy-300 mx-auto mb-4" />
              <p className="text-navy-600">No tienes simulaciones anteriores</p>
              <Button className="mt-4" onClick={() => { setShowHistory(false); startNewSimulation(); }}>
                Iniciar primera simulación
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session, idx) => (
                <motion.div
                  key={session.session_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-xl border border-navy-100 p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        session.final_score >= 70 ? 'bg-emerald-100' : 
                        session.final_score >= 50 ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        {session.final_score >= 70 ? (
                          <Trophy className="w-6 h-6 text-emerald-600" />
                        ) : session.final_score >= 50 ? (
                          <Target className="w-6 h-6 text-amber-600" />
                        ) : (
                          <TrendingUp className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-navy-900">
                          Simulación #{sessions.length - idx}
                        </p>
                        <p className="text-sm text-navy-500">
                          {new Date(session.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-sm text-navy-500">Preguntas</p>
                        <p className="font-bold text-navy-900">{session.question_count || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-navy-500">Promedio</p>
                        <p className="font-bold text-navy-900">{(session.average_score || 0).toFixed(1)}/10</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-navy-500">Aprobación</p>
                        <p className={`font-bold text-2xl ${
                          session.final_score >= 70 ? 'text-emerald-600' :
                          session.final_score >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {session.final_score ? `${session.final_score}%` : '-'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        session.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {session.status === 'completed' ? 'Completada' : 'En progreso'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Main simulator view
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-navy-900">Simulador de Entrevista</h1>
              <p className="text-navy-500 text-sm">Practica con nuestra IA consular</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setShowHistory(true)}>
              <History className="w-4 h-4 mr-2" />
              Historial
            </Button>
            
            {/* Attempts badge */}
            {!isUnlimited && remainingAttempts !== null && (
              <div className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
                {remainingAttempts} intentos restantes
              </div>
            )}
            {isUnlimited && (
              <div className="bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1">
                <Crown className="w-4 h-4" />
                Ilimitado
              </div>
            )}
          </div>
        </div>

        {/* No simulation started */}
        {messages.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-navy-100 p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-gold-500 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Bot className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-xl font-bold text-navy-900 mb-2">¿Listo para practicar?</h2>
            <p className="text-navy-600 mb-6 max-w-md mx-auto">
              Simularemos una entrevista real de visa B1/B2. Responderás 8 preguntas y recibirás 
              feedback instantáneo con un porcentaje de aprobación al final.
            </p>
            <Button onClick={startNewSimulation} size="lg" className="bg-gold-500 hover:bg-gold-600">
              <Zap className="w-5 h-5 mr-2" />
              Iniciar Simulación
            </Button>
          </motion.div>
        )}

        {/* Simulation in progress */}
        {messages.length > 0 && (
          <div className="bg-white rounded-2xl border border-navy-100 overflow-hidden">
            {/* Progress bar */}
            <div className="p-4 bg-navy-50 border-b border-navy-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-navy-700">
                  Pregunta {questionNumber} de 8
                </span>
                <span className="text-sm text-navy-500">
                  Promedio: <span className={getScoreColor(parseFloat(averageScore))}>{averageScore}/10</span>
                </span>
              </div>
              <Progress value={(questionNumber / 8) * 100} className="h-2" />
            </div>

            {/* Chat messages */}
            <ScrollArea className="h-[400px] p-4" ref={scrollRef}>
              <div className="space-y-4">
                <AnimatePresence>
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.role === "assistant" && (
                        <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-navy-600" />
                        </div>
                      )}
                      <div className={`max-w-[80%] rounded-2xl p-4 ${
                        msg.role === "user" 
                          ? "bg-gold-500 text-white" 
                          : "bg-navy-50 text-navy-900"
                      }`}>
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        {msg.score && msg.role === "assistant" && (
                          <div className="mt-2 pt-2 border-t border-navy-200 text-xs">
                            Tu respuesta: <span className={getScoreColor(msg.score)}>{getScoreStars(msg.score)}</span>
                          </div>
                        )}
                      </div>
                      {msg.role === "user" && (
                        <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-gold-600" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-navy-600" />
                    </div>
                    <div className="bg-navy-50 rounded-2xl p-4">
                      <Loader2 className="w-5 h-5 animate-spin text-navy-400" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Final result */}
            {isFinished && approvalPercentage !== null && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-gradient-to-r from-navy-900 to-navy-800 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg mb-1">Resultado Final</h3>
                    <p className="text-navy-300 text-sm">Basado en tu desempeño en la entrevista</p>
                  </div>
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${
                      approvalPercentage >= 70 ? 'text-emerald-400' :
                      approvalPercentage >= 50 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {approvalPercentage}%
                    </div>
                    <p className="text-sm text-navy-300">Probabilidad de aprobación</p>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <Button onClick={startNewSimulation} variant="secondary" className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Nueva Simulación
                  </Button>
                  <Button onClick={() => setShowHistory(true)} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Ver Historial
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Travel Promo Banner - Show after simulation is finished */}
            {isFinished && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-4 bg-navy-50"
              >
                <p className="text-sm text-navy-600 font-medium mb-2">
                  🎯 Tip: Tener una reservación de viaje demuestra planes concretos en tu entrevista real
                </p>
                <TravelPromoBanner vendedorInfo={user?.vendedor_info} userRole={user?.role} />
              </motion.div>
            )}

            {/* Input */}
            {!isFinished && (
              <div className="p-4 border-t border-navy-100">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={questionNumber >= 6 ? 'Responde o escribe "terminar" para ver tu evaluación...' : "Escribe tu respuesta..."}
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={loading || !input.trim()}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {questionNumber >= 6 && (
                  <p className="text-xs text-navy-500 mt-2">
                    Tip: Escribe "terminar" para finalizar y ver tu evaluación completa
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InterviewSimulator;
